"use client";

import { motion } from "motion/react";
import { Check, Zap, ArrowRight, X as XIcon, Sparkles } from "lucide-react";
import Link from "next/link";
import { useState, useEffect } from "react";

type TierKey = "free" | "starter" | "creator" | "studio" | "pro" | "teams";

interface TierTheme {
  cardBg: string;
  badge: string;
  priceGrad: string;
  check: string;
  checkText: string;
  ribbon: string;
  ribbonGlow: string;
  zap: string;
  button: string;
  hoverRing: string;
  hoverShadow: string;
}

const TIER_THEMES: Record<TierKey, TierTheme> = {
  free: {
    cardBg: "bg-gradient-to-b from-emerald-50/70 to-white dark:from-emerald-950/30 dark:to-slate-900/50",
    badge: "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/30",
    priceGrad: "from-emerald-500 to-teal-600 dark:from-emerald-300 dark:to-teal-300",
    check: "bg-emerald-100 dark:bg-emerald-500/15 dark:ring-1 dark:ring-emerald-500/30",
    checkText: "text-emerald-600 dark:text-emerald-400",
    ribbon: "from-emerald-400 to-teal-500",
    ribbonGlow: "shadow-emerald-500/40",
    zap: "text-emerald-500 dark:text-emerald-400",
    button: "bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:shadow-lg hover:shadow-emerald-500/40",
    hoverRing: "ring-emerald-400/60 dark:ring-emerald-500/50",
    hoverShadow: "shadow-emerald-200/60 dark:shadow-emerald-500/20",
  },
  starter: {
    cardBg: "bg-white dark:bg-slate-900/40",
    badge: "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800/80 dark:text-slate-300 dark:ring-slate-700",
    priceGrad: "from-slate-800 to-slate-950 dark:from-slate-100 dark:to-white",
    check: "bg-emerald-100 dark:bg-emerald-500/15 dark:ring-1 dark:ring-emerald-500/30",
    checkText: "text-emerald-600 dark:text-emerald-400",
    ribbon: "from-slate-400 to-slate-600",
    ribbonGlow: "shadow-slate-400/20 dark:shadow-slate-500/30",
    zap: "text-slate-500 dark:text-slate-400",
    button: "bg-slate-100 text-slate-800 hover:bg-slate-200 ring-1 ring-slate-200 dark:bg-white/10 dark:text-slate-100 dark:hover:bg-white/20 dark:ring-white/10",
    hoverRing: "ring-slate-400/50 dark:ring-slate-400/40",
    hoverShadow: "shadow-slate-200/60 dark:shadow-slate-500/20",
  },
  creator: {
    cardBg: "bg-gradient-to-b from-cyan-50/70 to-white dark:from-cyan-950/40 dark:to-slate-900/50",
    badge: "bg-cyan-100 text-cyan-700 ring-1 ring-cyan-200 dark:bg-cyan-500/15 dark:text-cyan-300 dark:ring-cyan-500/30",
    priceGrad: "from-cyan-500 to-teal-500 dark:from-cyan-300 dark:to-teal-300",
    check: "bg-emerald-100 dark:bg-emerald-500/15 dark:ring-1 dark:ring-emerald-500/30",
    checkText: "text-emerald-600 dark:text-emerald-400",
    ribbon: "from-cyan-400 to-teal-500",
    ribbonGlow: "shadow-cyan-500/40",
    zap: "text-cyan-500 dark:text-cyan-400",
    button: "bg-gradient-to-r from-cyan-500 to-teal-500 text-white hover:shadow-lg hover:shadow-cyan-500/40",
    hoverRing: "ring-cyan-400/60 dark:ring-cyan-500/50",
    hoverShadow: "shadow-cyan-200/60 dark:shadow-cyan-500/20",
  },
  studio: {
    cardBg: "bg-gradient-to-b from-blue-50/70 to-white dark:from-blue-950/40 dark:to-slate-900/50",
    badge: "bg-blue-100 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/30",
    priceGrad: "from-blue-500 to-indigo-600 dark:from-blue-300 dark:to-indigo-300",
    check: "bg-emerald-100 dark:bg-emerald-500/15 dark:ring-1 dark:ring-emerald-500/30",
    checkText: "text-emerald-600 dark:text-emerald-400",
    ribbon: "from-blue-400 to-indigo-500",
    ribbonGlow: "shadow-blue-500/40",
    zap: "text-blue-500 dark:text-blue-400",
    button: "bg-slate-100 text-slate-800 hover:bg-slate-200 ring-1 ring-slate-200 dark:bg-white/10 dark:text-slate-100 dark:hover:bg-white/20 dark:ring-white/10",
    hoverRing: "ring-blue-400/60 dark:ring-blue-500/50",
    hoverShadow: "shadow-blue-200/60 dark:shadow-blue-500/20",
  },
  pro: {
    cardBg: "bg-gradient-to-b from-purple-50/70 to-white dark:from-purple-950/40 dark:to-slate-900/50",
    badge: "bg-purple-100 text-purple-700 ring-1 ring-purple-200 dark:bg-purple-500/15 dark:text-purple-300 dark:ring-purple-500/30",
    priceGrad: "from-purple-500 to-fuchsia-600 dark:from-purple-300 dark:to-fuchsia-300",
    check: "bg-emerald-100 dark:bg-emerald-500/15 dark:ring-1 dark:ring-emerald-500/30",
    checkText: "text-emerald-600 dark:text-emerald-400",
    ribbon: "from-purple-400 to-fuchsia-500",
    ribbonGlow: "shadow-purple-500/40",
    zap: "text-purple-500 dark:text-purple-400",
    button: "bg-slate-100 text-slate-800 hover:bg-slate-200 ring-1 ring-slate-200 dark:bg-white/10 dark:text-slate-100 dark:hover:bg-white/20 dark:ring-white/10",
    hoverRing: "ring-purple-400/60 dark:ring-purple-500/50",
    hoverShadow: "shadow-purple-200/60 dark:shadow-purple-500/20",
  },
  teams: {
    cardBg: "bg-gradient-to-b from-amber-50/70 to-white dark:from-amber-950/30 dark:to-slate-900/50",
    badge: "bg-amber-100 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30",
    priceGrad: "from-amber-500 to-orange-500 dark:from-amber-300 dark:to-orange-300",
    check: "bg-emerald-100 dark:bg-emerald-500/15 dark:ring-1 dark:ring-emerald-500/30",
    checkText: "text-emerald-600 dark:text-emerald-400",
    ribbon: "from-amber-400 to-yellow-500",
    ribbonGlow: "shadow-amber-500/40",
    zap: "text-amber-500 dark:text-amber-400",
    button: "bg-amber-50 text-amber-700 ring-1 ring-amber-300 hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-400/40 dark:hover:bg-amber-500/20",
    hoverRing: "ring-amber-400/60 dark:ring-amber-500/50",
    hoverShadow: "shadow-amber-200/60 dark:shadow-amber-500/20",
  },
};

interface PlanData {
  tier: TierKey;
  label: string;
  price: number | null;
  original: number | null;
  credits: number | null;
  audio: string;
  ribbon: string;
  features: string[];
  disabled?: string[];
  buttonText: string;
  showZap?: boolean;
  customLabel?: string;
  customSub?: string;
  freeTrial?: boolean;
}

const PLANS: PlanData[] = [
  {
    tier: "free",
    label: "FREE TRIAL",
    price: 0,
    original: null,
    credits: 10000,
    audio: "~10 min of audio",
    ribbon: "TRY FREE",
    features: [],
    buttonText: "Get Started",
    showZap: true,
    freeTrial: true,
  },
  {
    tier: "starter",
    label: "STARTER",
    price: 5,
    original: 9,
    credits: 60000,
    audio: "~60 min of audio",
    ribbon: "",
    features: ["Single voice generation", "2,600+ AI voices", "25+ languages", "No 5,000-character limit", "MP3 128kbps export", "Commercial license"],
    disabled: ["Dialogue mode", "Voice cloning"],
    buttonText: "Get Starter",
  },
  {
    tier: "creator",
    label: "CREATOR",
    price: 25,
    original: 40,
    credits: 200000,
    audio: "~200 min of audio",
    ribbon: "MOST POPULAR",
    features: ["Everything in Starter", "Dialogue mode", "Multi-language", "1 voice clone", "Voice effects", "Emotional controls", "Creaneo AI assistant", "Edit panel (all tools)", "MP3 192kbps HD", "No character limit ∞", "Credit rollover (2 months)"],
    buttonText: "Get Creator",
    showZap: true,
  },
  {
    tier: "studio",
    label: "STUDIO",
    price: 59,
    original: 89,
    credits: 500000,
    audio: "~8 hours of audio",
    ribbon: "NEW",
    features: ["Everything in Creator", "Video dubbing", "3 voice clones", "Multi-track editor", "Background music mixer", "WAV export", "Priority queue", "Credit rollover (2 months)"],
    buttonText: "Get Studio",
  },
  {
    tier: "pro",
    label: "PRO",
    price: 129,
    original: 199,
    credits: 1000000,
    audio: "~16 hours of audio",
    ribbon: "",
    features: ["Everything in Studio", "10 voice clones", "PCM 44.1kHz studio quality", "Webhook integration", "Priority support", "Credit rollover (2 months)"],
    buttonText: "Get Pro",
  },
];

export function Pricing() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setLoggedIn(!!localStorage.getItem("speakroar-user"));
  }, []);

  const ctaHref = mounted && loggedIn ? "/buy-credits" : "/register";

  return (
    <section id="fiyatlar" className="relative py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-50/40 via-white to-cyan-50/30 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950 overflow-hidden">
      {/* Dark-mode ambient glows */}
      <div aria-hidden className="hidden dark:block pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/4 w-[480px] h-[480px] rounded-full bg-purple-500/10 blur-[120px]" />
        <div className="absolute top-1/2 right-0 w-[420px] h-[420px] rounded-full bg-cyan-500/10 blur-[120px]" />
      </div>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4">
            <span className="bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 dark:from-purple-400 dark:via-fuchsia-400 dark:to-pink-400 bg-clip-text text-transparent dark:drop-shadow-[0_0_30px_rgba(217,70,239,0.25)]">
              Choose your plan
            </span>
          </h2>
          <p className="text-neutral-600 dark:text-slate-400 text-base max-w-xl mx-auto font-medium">
            Start free, scale as you grow. All plans include <span className="font-bold text-neutral-900 dark:text-white">2,600+ AI voices</span> in <span className="font-bold text-neutral-900 dark:text-white">25+ languages</span>.
          </p>
        </motion.div>

        {/* Plans grid (5 cards) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-7xl mx-auto mb-12">
          {PLANS.map((plan, i) => {
            const t = TIER_THEMES[plan.tier];
            const isTeams = plan.tier === "teams";
            return (
              <motion.div
                key={plan.tier}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                whileHover={{ y: -10, scale: 1.03, transition: { type: "spring", stiffness: 300, damping: 22 } }}
                whileTap={{ scale: 0.98, transition: { duration: 0.1 } }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6, type: "spring", stiffness: 100 }}
                className="relative pt-7 cursor-pointer"
              >
                {plan.ribbon && (
                  <div className={`absolute -top-1 left-1/2 -translate-x-1/2 px-4 py-1 rounded-t-xl rounded-b-md bg-gradient-to-r ${t.ribbon} text-[10px] font-black text-white tracking-widest shadow-lg ${t.ribbonGlow} z-10 flex items-center gap-1`}>
                    {plan.ribbon === "COMING SOON" && <Sparkles size={9} />} {plan.ribbon}
                  </div>
                )}

                <div className={`relative p-6 rounded-3xl overflow-hidden flex flex-col h-full ${t.cardBg} ring-1 ring-neutral-200/70 dark:ring-white/10 shadow-md dark:shadow-black/20 transition-all duration-300`}>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full ${t.badge} text-[10px] font-black tracking-widest mb-5 self-start`}>
                    {plan.label}
                  </div>

                  {isTeams ? (
                    <>
                      <div className={`text-5xl font-black bg-gradient-to-r ${t.priceGrad} bg-clip-text text-transparent leading-none mb-3`}>
                        {plan.customLabel}
                      </div>
                      <p className="text-sm text-neutral-600 dark:text-slate-300 font-medium mb-6">{plan.customSub}</p>
                    </>
                  ) : (
                    <>
                      {plan.original != null && (
                        <div className="text-sm text-neutral-400 dark:text-slate-500 line-through font-semibold mb-1">${plan.original}</div>
                      )}
                      <div className="flex items-baseline gap-1 mb-5">
                        <span className={`text-5xl font-black bg-gradient-to-r ${t.priceGrad} bg-clip-text text-transparent leading-none`}>${plan.price}</span>
                        <span className="text-sm text-neutral-400 dark:text-slate-500 font-medium">/mo</span>
                      </div>

                      <div className="flex items-center gap-1.5 mb-1">
                        <Zap size={14} className={t.zap} fill="currentColor" />
                        <span className="text-sm font-black text-neutral-900 dark:text-white">{plan.credits!.toLocaleString("en-US")} credits/mo</span>
                      </div>
                      <div className="text-xs text-neutral-600 dark:text-slate-300 mb-1 font-medium ml-5">{plan.audio}</div>
                    </>
                  )}

                  <div className="h-px bg-gradient-to-r from-transparent via-neutral-200 dark:via-white/10 to-transparent my-4" />

                  {plan.freeTrial ? (
                    <div className="flex-1 flex flex-col gap-3">
                      <div className="flex items-center gap-2 text-xs text-neutral-700 dark:text-slate-300 font-semibold">
                        <div className={`w-4 h-4 rounded-full ${t.check} flex items-center justify-center flex-shrink-0`}>
                          <Check size={10} className={t.checkText} strokeWidth={3} />
                        </div>
                        All features included
                      </div>
                      <div className="mt-2 flex flex-col gap-2">
                        <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-200 dark:ring-emerald-500/20">
                          <span className="text-lg">⏱</span>
                          <div>
                            <div className="text-xs font-black text-emerald-700 dark:text-emerald-300">3-day trial</div>
                            <div className="text-[10px] text-emerald-600/80 dark:text-emerald-400/70">Full access, 3 days</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-50 dark:bg-white/5 ring-1 ring-slate-200 dark:ring-white/10">
                          <span className="text-lg">🔓</span>
                          <div className="text-[10px] font-semibold text-neutral-500 dark:text-slate-400">No credit card required</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2.5 flex-1">
                      {plan.features.map((f) => (
                        <div key={f} className="flex items-center gap-2 text-xs text-neutral-700 dark:text-slate-300 font-medium">
                          <div className={`w-4 h-4 rounded-full ${t.check} flex items-center justify-center flex-shrink-0`}>
                            <Check size={10} className={t.checkText} strokeWidth={3} />
                          </div>
                          {f}
                        </div>
                      ))}
                      {plan.disabled?.map((f) => (
                        <div key={f} className="flex items-center gap-2 text-xs text-neutral-400 dark:text-slate-600 font-medium line-through">
                          <div className="w-4 h-4 rounded-full bg-neutral-100 dark:bg-white/5 ring-1 ring-neutral-200 dark:ring-white/10 flex items-center justify-center flex-shrink-0">
                            <XIcon size={9} className="text-neutral-400 dark:text-slate-600" strokeWidth={3} />
                          </div>
                          {f}
                        </div>
                      ))}
                    </div>
                  )}

                  {isTeams ? (
                    <a
                      href="mailto:hello@speakroar.com?subject=Teams%20plan%20waitlist"
                      className={`mt-6 w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all ${t.button}`}
                    >
                      {plan.buttonText} <ArrowRight size={14} />
                    </a>
                  ) : (
                    <Link
                      href={plan.tier === "free" ? "/login" : ctaHref}
                      className={`mt-6 w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all ${t.button}`}
                    >
                      {plan.showZap && <Zap size={14} fill="currentColor" />} {plan.buttonText} <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom info pill */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="max-w-3xl mx-auto"
        >
          <div className="bg-white/70 dark:bg-white/5 backdrop-blur-xl ring-1 ring-neutral-200/70 dark:ring-white/10 rounded-2xl px-6 py-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-semibold text-neutral-600 dark:text-slate-200">
            <span>Multilingual: 2 credits / character</span>
            <span className="text-neutral-300 dark:text-slate-700">&middot;</span>
            <span>Flash models: 1 credit / character</span>
            <span className="text-neutral-300 dark:text-slate-700">&middot;</span>
            <span>SSL encrypted</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
